import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { sha256 } from "js-sha256";
import { useStaff } from "../lib/staff.ts";

type ManifestEntry = {
  version: string;
  url: string;
  size: number;
  sha256: string;
  release_notes?: string;
};

type ChannelState = {
  exists: boolean;
  manifest?: ManifestEntry[] | null;
};

async function getReleases(): Promise<{ stable: ChannelState; beta: ChannelState }> {
  const r = await fetch("/v1/staff/releases", { credentials: "include" });
  if (!r.ok) throw new Error(`${r.status}`);
  return r.json();
}

async function promote(): Promise<{ promoted: ManifestEntry[] }> {
  const r = await fetch("/v1/staff/releases/promote", {
    method: "POST",
    credentials: "include",
  });
  if (!r.ok) throw new Error(`${r.status}`);
  return r.json();
}

async function deleteEntry(args: {
  channel: "beta" | "stable"; version: string; force?: boolean;
}): Promise<{ blobDeleted: boolean; remaining: number }> {
  const r = await fetch("/v1/staff/releases/delete-entry", {
    method: "POST", credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(args),
  });
  if (!r.ok) throw new Error(await errMessage(r));
  return r.json();
}

// Chunked multipart upload to /v1/staff/releases/upload/{init,part,
// complete,abort}.
//
// Flow:
//   0) Pre-hash the whole file (single sequential pass over 8 MB slices
//      so the JS heap stays flat). Surfaces as a "Hashing" phase in the
//      UI so the user knows why there's silence before chunks start.
//   1) init — server checks the sha against both manifests and 409s on
//      duplicate-version OR duplicate-sha BEFORE we waste any upload
//      bandwidth. Returns uploadId + partSizeBytes (50 MB).
//   2) parts — slice file, POST each chunk; no per-chunk hashing since
//      we already have the full sha.
//   3) complete — server assembles the multipart, writes the manifest
//      entry with sha + size + published_at.
// abort tears down on cancel/failure.
async function uploadMultipart(args: {
  file: File;
  version: string;
  releaseNotes: string;
  onHashProgress?: (hashed: number, total: number) => void;
  onUploadProgress?: (uploaded: number, total: number) => void;
}): Promise<{ entry: ManifestEntry }> {
  const { file, version, releaseNotes, onHashProgress, onUploadProgress } = args;
  const total = file.size;

  // 0) Pre-hash. 8 MB slice size keeps memory tiny; ~200 MB/s in JS so
  // an 850 MB file is ~4 s. yield to the event loop between slices so
  // the progress bar can repaint.
  const HASH_SLICE = 8 * 1024 * 1024;
  const hasher = sha256.create();
  for (let off = 0; off < total; off += HASH_SLICE) {
    const end = Math.min(off + HASH_SLICE, total);
    const buf = await file.slice(off, end).arrayBuffer();
    hasher.update(new Uint8Array(buf));
    onHashProgress?.(end, total);
    if (typeof MessageChannel !== "undefined") {
      // yield to repaint without piling timers
      await new Promise<void>((res) => {
        const ch = new MessageChannel();
        ch.port1.onmessage = () => res();
        ch.port2.postMessage(null);
      });
    }
  }
  const fileSha = hasher.hex();

  // 1) init — sha + version dedupe happen server-side.
  const initRes = await fetch("/v1/staff/releases/upload/init", {
    method: "POST", credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ version, sha256: fileSha }),
  });
  if (!initRes.ok) throw new Error(await errMessage(initRes));
  const { uploadId, key, partSizeBytes } = await initRes.json() as {
    uploadId: string; key: string; partSizeBytes: number;
  };

  // 2) parts — slice + upload, no hashing since done above.
  const parts: Array<{ partNumber: number; etag: string }> = [];
  const numParts = Math.max(1, Math.ceil(total / partSizeBytes));
  let uploaded = 0;

  try {
    for (let i = 0; i < numParts; i++) {
      const start = i * partSizeBytes;
      const end   = Math.min(start + partSizeBytes, total);
      const chunkBuf = await file.slice(start, end).arrayBuffer();

      const url = `/v1/staff/releases/upload/part?` +
        `key=${encodeURIComponent(key)}` +
        `&uploadId=${encodeURIComponent(uploadId)}` +
        `&partNumber=${i + 1}`;
      const r = await fetch(url, {
        method: "POST", credentials: "include",
        body: chunkBuf,
      });
      if (!r.ok) throw new Error(`part ${i + 1}: ${await errMessage(r)}`);
      const { etag } = await r.json() as { etag: string };
      parts.push({ partNumber: i + 1, etag });
      uploaded = end;
      onUploadProgress?.(uploaded, total);
    }

    // 3) complete — sha is the one we computed pre-init.
    const completeRes = await fetch("/v1/staff/releases/upload/complete", {
      method: "POST", credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        uploadId, key, version,
        sha256: fileSha,
        size: total,
        parts,
        releaseNotes,
      }),
    });
    if (!completeRes.ok) throw new Error(await errMessage(completeRes));
    return completeRes.json();
  } catch (e) {
    // Best-effort abort so the half-uploaded parts don't linger in R2.
    void fetch("/v1/staff/releases/upload/abort", {
      method: "POST", credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ uploadId, key }),
    }).catch(() => undefined);
    throw e;
  }
}

async function errMessage(r: Response): Promise<string> {
  try {
    const body = await r.json() as { error?: string; detail?: string };
    return body.error ? `${body.error}${body.detail ? `: ${body.detail}` : ""}` : `${r.status}`;
  } catch {
    return `${r.status}`;
  }
}

// Heuristic: pull "1.2.3" / "1.2.3-rc1" out of "btquick-1.2.3.btu" et al.
function guessVersionFromFilename(name: string): string {
  const m = name.match(/(\d+\.\d+\.\d+(?:-[A-Za-z0-9.-]+)?)/);
  return m ? m[1] : "";
}

function ManifestCard({
  title, channel, state, onDelete, deletingVersion,
}: {
  title: string;
  channel: "beta" | "stable";
  state?: ChannelState;
  onDelete?: (channel: "beta" | "stable", version: string, isTop: boolean) => void;
  deletingVersion?: string | null;
}) {
  return (
    <div className="card">
      <h3>{title}</h3>
      {!state || !state.exists
        ? <p className="muted">No manifest on file.</p>
        : !state.manifest
          ? <p className="muted">Manifest exists but couldn't be parsed.</p>
          : state.manifest.length === 0
            ? <p className="muted">Empty manifest (no releases).</p>
            : (
              <div className="table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Version</th><th>Size</th><th>SHA-256</th>
                      {onDelete && <th></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {state.manifest.map((e, i) => {
                      const isTop = i === 0;
                      const isDeleting = deletingVersion === e.version;
                      return (
                        <tr key={`${e.version}-${i}`}>
                          <td>
                            <code>{e.version}</code>
                            {isTop && <span className="muted" style={{ marginLeft: 8 }}>(current)</span>}
                          </td>
                          <td>{(e.size / (1024 * 1024)).toFixed(1)} MB</td>
                          <td><code className="code-xs">{e.sha256.slice(0, 16)}…</code></td>
                          {onDelete && (
                            <td>
                              <button
                                className="danger"
                                disabled={isDeleting}
                                onClick={() => onDelete(channel, e.version, isTop)}
                              >
                                {isDeleting ? "Deleting…" : "Delete"}
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
    </div>
  );
}

export function ReleasesPage() {
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadVersion, setUploadVersion] = useState("");
  const [uploadNotes, setUploadNotes] = useState("");
  const [uploadMsg, setUploadMsg] = useState<string | null>(null);
  const [hashProgress, setHashProgress] = useState<{ hashed: number; total: number } | null>(null);
  const [uploadProgress, setUploadProgress] = useState<{ uploaded: number; total: number } | null>(null);
  const [deletingVersion, setDeletingVersion] = useState<string | null>(null);
  const qc = useQueryClient();
  // Promote + upload + delete are admin-only on the server. Support /
  // developer roles get a read-only view of the manifests.
  const { role } = useStaff();
  const canPromote = role === "admin";
  const canUpload  = role === "admin";
  const canDelete  = role === "admin";

  const releases = useQuery({
    queryKey: ["staff-releases"],
    queryFn: getReleases,
  });

  const promoteMut = useMutation({
    mutationFn: promote,
    onSuccess: (data) => {
      const top = data.promoted?.[0];
      setResult(top
        ? `Promoted ${top.version} to stable.`
        : `Promoted (manifest empty).`);
      setConfirming(false);
      qc.invalidateQueries({ queryKey: ["staff-releases"] });
    },
    onError: (e) => {
      setResult(`Failed: ${String(e)}`);
      setConfirming(false);
    },
  });

  const deleteMut = useMutation({
    mutationFn: deleteEntry,
    onSuccess: (_data, vars) => {
      setResult(`Removed ${vars.version} from ${vars.channel}. (The R2 blob is kept; the daily cleanup cron removes it once no manifest references it AND it's >7 days old.)`);
      setDeletingVersion(null);
      qc.invalidateQueries({ queryKey: ["staff-releases"] });
    },
    onError: (e) => {
      setResult(`Delete failed: ${(e as Error).message}`);
      setDeletingVersion(null);
    },
  });

  const handleDelete = (channel: "beta" | "stable", version: string, isTop: boolean) => {
    const warn = isTop
      ? `Delete CURRENT ${channel} release ${version}?\n\nDevices on this channel will fall back to the next-newest entry (or have no update path if this is the only one). The R2 blob is kept for 7 days in case you want to undo. Continue?`
      : `Delete ${version} from ${channel}?\n\nThis removes the manifest entry. The R2 blob is kept until the daily cleanup cron sweeps it (≥7 days unreferenced).`;
    if (!confirm(warn)) return;
    setDeletingVersion(version);
    deleteMut.mutate({ channel, version });
  };

  const uploadMut = useMutation({
    mutationFn: (args: { file: File; version: string; releaseNotes: string }) =>
      uploadMultipart({
        ...args,
        onHashProgress:   (hashed, total)   => setHashProgress({ hashed, total }),
        onUploadProgress: (uploaded, total) => {
          // Once any chunk has uploaded, hashing is done — clear that bar
          // so the user sees only the active phase.
          setHashProgress(null);
          setUploadProgress({ uploaded, total });
        },
      }),
    onSuccess: (data) => {
      setUploadMsg(`Published ${data.entry.version} to beta (${(data.entry.size / (1024 * 1024)).toFixed(1)} MB).`);
      setUploadFile(null);
      setUploadVersion("");
      setUploadNotes("");
      setHashProgress(null);
      setUploadProgress(null);
      qc.invalidateQueries({ queryKey: ["staff-releases"] });
    },
    onError: (e) => {
      setUploadMsg(`Failed: ${(e as Error).message}`);
      setHashProgress(null);
      setUploadProgress(null);
    },
  });

  return (
    <>
      <h1>Releases</h1>
      <p className="muted">
        Channel manifests live at <code>releases/&lt;channel&gt;.json</code> in R2.
        Devices on the matching <code>BT_CloudUpdateChannel</code> auto-check
        daily and surface a Download button when a newer version is published.
      </p>

      {releases.isLoading && <p className="muted">Loading…</p>}
      {releases.error && <p className="err">Couldn't load releases — {(releases.error as Error).message}</p>}

      <div className="stat-grid">
        <ManifestCard
          title="Stable" channel="stable" state={releases.data?.stable}
          onDelete={canDelete ? handleDelete : undefined}
          deletingVersion={deletingVersion}
        />
        <ManifestCard
          title="Beta"   channel="beta"   state={releases.data?.beta}
          onDelete={canDelete ? handleDelete : undefined}
          deletingVersion={deletingVersion}
        />
      </div>

      {!canPromote && (
        <p className="muted mt">
          Promotion + upload are admin-only. Your role can view the manifests
          but not change which version is on the <code>stable</code> channel.
        </p>
      )}

      {canUpload && (
      <div className="card mt">
        <h3>Upload a new release</h3>
        <p className="muted">
          Goes straight to the <code>beta</code> channel as the newest entry.
          The artifact is signed Ed25519 by the same key the device already
          trusts; we just stash it in R2 and update the manifest. Once
          verified on beta devices, click "Promote" below.
        </p>
        <div className="formstack" style={{ maxWidth: 560 }}>
          <label>
            <span style={{ display: "block", marginBottom: "0.25rem" }}>
              <code>.btu</code> artifact
            </span>
            <input
              type="file"
              accept=".btu,application/octet-stream"
              onChange={(e) => {
                const f = e.target.files?.[0] ?? null;
                setUploadFile(f);
                // Only override the manually-typed version if the input
                // field is still empty — we don't want a re-pick of the
                // same file to undo a user edit.
                if (f && !uploadVersion) {
                  const guess = guessVersionFromFilename(f.name);
                  if (guess) setUploadVersion(guess);
                }
              }}
            />
          </label>
          <label>
            <span style={{ display: "block", marginBottom: "0.25rem" }}>Version (semver)</span>
            <input
              placeholder="1.0.5"
              value={uploadVersion}
              onChange={(e) => setUploadVersion(e.target.value.trim())}
            />
          </label>
          <label>
            <span style={{ display: "block", marginBottom: "0.25rem" }}>
              Release notes <span className="muted">(optional)</span>
            </span>
            <textarea
              rows={3}
              placeholder="What changed in this build…"
              value={uploadNotes}
              onChange={(e) => setUploadNotes(e.target.value)}
            />
          </label>
          <div className="row">
            <button
              disabled={!uploadFile || !uploadVersion || uploadMut.isPending}
              onClick={() => uploadFile && uploadMut.mutate({
                file: uploadFile,
                version: uploadVersion,
                releaseNotes: uploadNotes,
              })}
            >
              {!uploadMut.isPending ? "Publish to beta"
                : hashProgress ? "Hashing…"
                : "Uploading…"}
            </button>
            {uploadFile && (
              <span className="muted">
                {uploadFile.name} · {(uploadFile.size / (1024 * 1024)).toFixed(1)} MB
              </span>
            )}
          </div>
          {(hashProgress || uploadProgress) && (() => {
            const phase = hashProgress ? "hash" : "upload";
            const done  = hashProgress ? hashProgress.hashed   : uploadProgress!.uploaded;
            const total = hashProgress ? hashProgress.total    : uploadProgress!.total;
            const pct   = total > 0 ? (done / total) * 100 : 0;
            const label = phase === "hash" ? "Hashing" : "Uploading";
            return (
              <div style={{ marginTop: "0.5rem" }}>
                <div style={{
                  height: 8,
                  background: "rgba(255,255,255,0.08)",
                  borderRadius: 4,
                  overflow: "hidden",
                }}>
                  <div style={{
                    width: `${pct.toFixed(1)}%`,
                    height: "100%",
                    background: phase === "hash" ? "var(--accent-2, #f6a623)" : "var(--accent, #4f8cff)",
                    transition: "width 0.2s ease",
                  }} />
                </div>
                <p className="muted" style={{ marginTop: "0.25rem", fontSize: "0.85em" }}>
                  {label}: {(done / (1024 * 1024)).toFixed(1)} /
                  {" "}{(total / (1024 * 1024)).toFixed(1)} MB
                  {" "}({pct.toFixed(1)}%)
                </p>
              </div>
            );
          })()}
          {uploadMsg && (
            <p className={uploadMsg.startsWith("Failed") ? "err" : ""}>{uploadMsg}</p>
          )}
        </div>
      </div>
      )}

      {canPromote && (
      <div className="card mt">
        <h3>Promote beta → stable</h3>
        <p className="muted">
          Copies the current beta manifest over the stable manifest. All
          devices on the <code>stable</code> channel will pick up the change
          on their next daily auto-check.
        </p>
        {!confirming && (
          <button
            disabled={!releases.data?.beta?.exists}
            onClick={() => setConfirming(true)}
          >
            Promote beta to stable
          </button>
        )}
        {confirming && (
          <div className="row">
            <button
              className="danger"
              disabled={promoteMut.isPending}
              onClick={() => promoteMut.mutate()}
            >
              {promoteMut.isPending ? "Promoting…" : "Yes, promote"}
            </button>
            <button className="secondary" onClick={() => setConfirming(false)}>
              Cancel
            </button>
          </div>
        )}
        {result && <p className="mt">{result}</p>}
      </div>
      )}
    </>
  );
}
